import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, ExternalLink, FileWarning, Loader2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { analyzeStoredJob, asArray, fetchMasterProfile, refineStoredJob, safeError } from '../data/candidateApi';

const CHECK_FIT_JOB_KEY = 'jobmatchpro:selected-stored-job-id';
const CHECK_FIT_JOB_SNAPSHOT_KEY = 'jobmatchpro:selected-stored-job-snapshot';

function readStoredJobId(searchParams) {
  const fromUrl = searchParams.get('job_id');
  if (fromUrl) return fromUrl;
  try { return window.sessionStorage.getItem(CHECK_FIT_JOB_KEY) || ''; } catch (_) { return ''; }
}

function readJobSnapshot() {
  try {
    const raw = window.sessionStorage.getItem(CHECK_FIT_JOB_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function formatDate(value) {
  if (!value) return 'Not available';
  try { return new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(value)); } catch (_) { return 'Not available'; }
}

function normalizeCandidateResult(payload) {
  const result = payload?.candidate_result || payload?.result || payload || null;
  return result ? {
    ...result,
    company: result.company || result?.job?.company,
    location: result.location || result?.job?.location,
    job_title: result.job_title || result?.job?.job_title || result?.job?.title,
    job_url: result.job_url || result?.job?.job_url,
    refinement_context_id: payload?.refinement_context_id || result.refinement_context_id,
    submission_id: payload?.submission_id || result.submission_id,
    job_result_id: payload?.job_result_id || result.job_result_id,
  } : null;
}

function needsRefinement(result) {
  const status = String(result?.status || '').toUpperCase();
  return status.includes('REFIN') || (asArray(result?.refinement_questions || result?.questions).length > 0 && !result?.verdict);
}

function questionsFor(result) {
  return asArray(result?.refinement_questions || result?.questions).slice(0, 2).map((question, index) => ({
    id: question.refinement_id || question.id || question.key || `question_${index + 1}`,
    label: question.reason || question.label || question.question || String(question || ''),
  }));
}

function verdictFor(result) {
  const value = String(result?.verdict || result?.fit || result?.qualitative_fit || '').trim();
  return ['Strong', 'Medium', 'Weak'].includes(value) ? value : null;
}

function provenanceLabel(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'cv') return 'From your CV';
  if (normalized === 'self_declared') return 'You told us';
  return 'Basis provided';
}

function requirementStatus(value) {
  const normalized = String(value || '').toLowerCase();
  if (['met', 'pass', 'yes', 'true'].includes(normalized)) return 'Met';
  if (['not_met', 'failed', 'fail', 'no', 'false'].includes(normalized)) return 'Not met';
  return 'Unknown';
}

function labelFromKey(value) {
  return String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function verdictTone(verdict, hasUnknowns = false) {
  if (verdict === 'Strong') return { accent: '#00A676', wash: '#E8F8F1', label: 'Strong fit result' };
  if (verdict === 'Weak') return { accent: '#E45858', wash: '#FDECEC', label: 'Weak fit result' };
  return { accent: '#F2B84B', wash: '#FFF4D7', label: hasUnknowns ? 'Medium result with important unknowns' : 'Medium fit result' };
}

function ProfileGate({ profile, onContinue, jobId }) {
  const state = profile?.state || 'MISSING';
  const masterProfilePath = jobId ? `/master-profile?job_id=${encodeURIComponent(jobId)}` : '/master-profile';
  if (state === 'CONFIRMED') {
    return (
      <section className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="check-fit-profile-confirmed">
        <CheckCircle className="h-7 w-7 text-emerald-700" aria-hidden="true" />
        <h2 className="mt-4 font-serif-display text-3xl font-medium">Using your confirmed Master Profile</h2>
        <p className="mt-3 text-sm text-[#5f6c64]">Last updated: {formatDate(profile?.last_updated_at)}</p>
        <Button type="button" className="btn-primary mt-6" onClick={onContinue} data-testid="start-fit-analysis-button">Check my fit</Button>
      </section>
    );
  }
  if (state === 'EXTRACTING') {
    return <section className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="check-fit-profile-extracting"><Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" /><h2 className="mt-4 font-serif-display text-3xl font-medium">Preparing your Master Profile</h2><p className="mt-3 text-sm text-[#5f6c64]">We’ll use it for fit once extraction is complete.</p></section>;
  }
  if (state === 'EXTRACTED_NOT_REVIEWED') {
    return <section className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="check-fit-profile-review-required"><AlertCircle className="h-7 w-7 text-[#7b694d]" aria-hidden="true" /><h2 className="mt-4 font-serif-display text-3xl font-medium">Review your Master Profile first</h2><p className="mt-3 text-sm text-[#5f6c64]">Fit analysis starts only after you confirm your reusable profile.</p><Link to={masterProfilePath} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="review-master-profile-link">Review Master Profile</Link></section>;
  }
  if (state === 'EXTRACTION_FAILED') {
    return <section className="border border-red-200 bg-red-50 p-6 text-red-800" data-testid="check-fit-profile-failed"><FileWarning className="h-7 w-7" aria-hidden="true" /><h2 className="mt-4 font-serif-display text-3xl font-medium">Master Profile setup failed</h2><p className="mt-3 text-sm">We cannot use that extraction as evidence. Recover manually before checking fit.</p><Link to={masterProfilePath} className="mt-6 inline-flex min-h-[44px] items-center border border-red-800 px-5 text-sm font-medium" data-testid="recover-master-profile-link">Recover manually</Link></section>;
  }
  return <section className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="check-fit-profile-missing"><AlertCircle className="h-7 w-7 text-[#7b694d]" aria-hidden="true" /><h2 className="mt-4 font-serif-display text-3xl font-medium">Set up your Master Profile once</h2><p className="mt-3 text-sm text-[#5f6c64]">First-time users add profile evidence before personalized fit analysis.</p><Link to={masterProfilePath} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="setup-master-profile-link">Set up Master Profile</Link></section>;
}

function RefinementPanel({ result, onSubmit, loading }) {
  const questions = questionsFor(result);
  const [answers, setAnswers] = useState(() => Object.fromEntries(questions.map((question) => [question.id, ''])));
  return (
    <section className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="fit-refinement-panel">
      <h2 className="font-serif-display text-3xl font-medium">We need a couple of details before we can give you a reliable fit result.</h2>
      <div className="mt-6 space-y-5">
        {questions.map((question, index) => (
          <label key={question.id} className="block" htmlFor={`refinement-${question.id}`} data-testid={`refinement-question-${index + 1}`}>
            <span className="text-sm font-medium">{question.label}</span>
            <Textarea id={`refinement-${question.id}`} value={answers[question.id] || ''} onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })} className="mt-2 min-h-[96px]" data-testid={`refinement-answer-${index + 1}`} />
            <Button type="button" variant="outline" className="mt-2" disabled={loading} onClick={() => setAnswers({ ...answers, [question.id]: 'unknown' })} data-testid={`refinement-dont-know-${index + 1}`}>I do not know</Button>
          </label>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button type="button" className="btn-primary" disabled={loading} onClick={() => onSubmit(answers)} data-testid="submit-refinement-button"><Send className="mr-2 h-4 w-4" /> Continue</Button>
      </div>
    </section>
  );
}

function FinalResult({ result }) {
  const verdict = verdictFor(result);
  const eligible = Boolean(result?.application_eligible) && verdict !== 'Weak';
  const evidence = asArray(result?.evidence || result?.strongest_evidence).slice(0, 3);
  const requirements = asArray(result?.hard_requirements || result?.requirements);
  const gaps = asArray(result?.gaps || result?.meaningful_gaps || result?.things_to_consider).slice(0, 3);
  const unknowns = asArray(result?.unknowns || result?.explicit_unknowns);
  const tone = verdictTone(verdict, unknowns.length > 0);
  return (
    <section className="jmp-result-reveal relative overflow-hidden border border-[#10231D] bg-[#FFFDF8] p-6 shadow-[0_16px_44px_rgba(16,35,29,0.12)]" style={{ borderTop: `5px solid ${tone.accent}`, borderRadius: '10px' }} data-testid="fit-final-result" aria-label={tone.label}>
      <p className="text-sm text-[#5f6c64]" data-testid="fit-result-company-location">{result?.company || 'Company not listed'} · {result?.location || 'Location not listed'}</p>
      <h2 className="mt-2 font-serif-display text-4xl font-medium" data-testid="fit-result-job-title">{result?.job_title || result?.title || 'Selected job'}</h2>
      <p className="mt-5 inline-flex items-center gap-2 border px-4 py-2 text-sm font-medium" style={{ backgroundColor: tone.wash, borderColor: tone.accent, color: '#10231D' }} data-testid="fit-result-verdict"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tone.accent }} aria-hidden="true" />{verdict}</p>
      <p className="mt-5 text-base leading-relaxed text-[#30473e]" data-testid="fit-result-summary">{result?.summary || result?.deciding_summary || 'This result is based on the available profile evidence.'}</p>
      {evidence.length > 0 && <div className="mt-6" data-testid="fit-result-evidence"><h3 className="text-sm font-semibold">Strongest evidence</h3><ul className="mt-3 grid gap-3 text-sm md:grid-cols-2">{evidence.map((item, index) => <li key={index} className="rounded-md border border-[#D7C9AF] bg-[#FFFDF8] p-3" data-testid={`fit-result-evidence-${index}`}>{item.label || item.text || item.summary} <span className="text-[#7b694d]">— {provenanceLabel(item.provenance || item.source)}</span></li>)}</ul></div>}
      {requirements.length > 0 && <div className="mt-6" data-testid="fit-result-requirements"><h3 className="text-sm font-semibold">Hard requirements</h3><ul className="mt-3 grid gap-2 text-sm md:grid-cols-3">{requirements.map((item, index) => { const status = requirementStatus(item.status || item.result); const statusColor = status === 'Met' ? '#00A676' : status === 'Not met' ? '#E45858' : '#F2B84B'; return <li key={index} className="border border-[#d8c8ab] bg-white/60 p-3" style={{ borderLeft: `4px solid ${statusColor}` }} data-testid={`fit-result-requirement-${index}`}><span className="block font-medium">{item.label || labelFromKey(item.key || item.name)}</span><span>{status}</span></li>; })}</ul></div>}
      {(gaps.length > 0 || unknowns.length > 0) && <div className="mt-6 rounded-md border border-[#D7C9AF] p-4" style={{ backgroundColor: unknowns.length > 0 ? '#FFF4D7' : '#FFFDF8' }} data-testid="fit-result-things-to-consider"><h3 className="text-sm font-semibold">Things to consider</h3><ul className="mt-3 space-y-2 text-sm">{gaps.map((gap, index) => <li key={`gap-${index}`} data-testid={`fit-result-gap-${index}`}>{gap.label || gap.text || gap}</li>)}{unknowns.map((unknown, index) => <li key={`unknown-${index}`} data-testid={`fit-result-unknown-${index}`}>Unknown: {unknown.label || unknown.text || unknown}</li>)}</ul></div>}
      <div className="mt-6 border-t border-[#d8c8ab] pt-5 text-sm text-[#5f6c64]" data-testid="fit-result-basis">Basis: {result?.basis || 'Master Profile and job requirements'} · Calculated on {formatDate(result?.calculated_on || result?.calculated_at)}</div>
      {result?.recommendation && <p className="mt-4 text-sm text-[#30473e]" data-testid="fit-result-recommendation">{result.recommendation}</p>}
      <div className="mt-6 flex flex-wrap gap-3" data-testid="fit-result-actions">
        {result?.job_url && <a href={result.job_url} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center px-4 text-sm" data-testid="fit-result-view-job-link">View job <ExternalLink className="ml-2 h-4 w-4" /></a>}
        <Link to="/my-jobs" className="btn-secondary inline-flex items-center px-4 text-sm" data-testid="fit-result-save-link">Save</Link>
        <Link to="/find-jobs" className="btn-secondary inline-flex items-center px-4 text-sm" data-testid="fit-result-not-for-me-link">Not for me</Link>
        {eligible && <Link to={`/application-package/${encodeURIComponent(result?.job_result_id || result?.id || '')}`} className="btn-primary inline-flex items-center px-4 text-sm" data-testid="fit-result-prepare-application-link">Prepare Application</Link>}
      </div>
      {!eligible && <p className="mt-4 text-sm text-[#5f6c64]" data-testid="fit-result-no-prepare-copy">Preparation is unavailable for this result, but the fit details above can help you decide what to do next.</p>}
    </section>
  );
}

function NoVerdict({ onRetry }) {
  return <section className="border border-red-200 bg-red-50 p-6 text-red-800" data-testid="fit-no-verdict"><h2 className="font-serif-display text-3xl font-medium">We couldn’t produce a reliable fit result from the available data.</h2><p className="mt-3 text-sm">Please try again or return to the job details.</p><div className="mt-6 flex gap-3"><Button type="button" variant="outline" onClick={onRetry} data-testid="fit-no-verdict-retry">Try again</Button><Link to="/find-jobs" className="inline-flex min-h-[44px] items-center border border-red-800 px-5 text-sm font-medium">Back to Find Jobs</Link></div></section>;
}

export default function FindJobsPage() {
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState('');
  const jobId = useMemo(() => readStoredJobId(searchParams), [searchParams]);
  const jobSnapshot = useMemo(() => readJobSnapshot(), []);

  useEffect(() => {
    let alive = true;
    async function load() {
      try { const payload = await fetchMasterProfile(); if (alive) setProfile(payload); }
      catch (err) { if (alive) setError(safeError(err)); }
      finally { if (alive) setProfileLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, []);

  async function startAnalysis() {
    if (!jobId) { setError('Choose a job before checking fit.'); return; }
    setAnalysisLoading(true);
    setError('');
    try { setResult(normalizeCandidateResult(await analyzeStoredJob(jobId))); }
    catch (err) { setError(safeError(err, 'We could not complete fit analysis right now.')); }
    finally { setAnalysisLoading(false); }
  }

  async function submitRefinement(answers) {
    setAnalysisLoading(true);
    setError('');
    try { setResult(normalizeCandidateResult(await refineStoredJob(jobId, result, answers))); }
    catch (err) { setError(safeError(err, 'We could not continue fit analysis right now.')); }
    finally { setAnalysisLoading(false); }
  }

  return (
    <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="check-fit-page">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Stage 2</p>
        <h1 className="mt-3 font-serif-display text-4xl font-medium sm:text-5xl" data-testid="find-jobs-heading">Understand my fit</h1>
        {jobSnapshot && <p className="mt-4 text-sm text-[#5f6c64]" data-testid="selected-job-summary">{jobSnapshot.title} · {jobSnapshot.company} · {jobSnapshot.location}</p>}
        {error && <div className="mt-6 border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="fit-analysis-error">{error}</div>}
        <div className="mt-8">
          {profileLoading && <div className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="check-fit-profile-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading profile…</div>}
          {!profileLoading && !result && <ProfileGate profile={profile} onContinue={startAnalysis} jobId={jobId} />}
          {analysisLoading && <div className="mt-6 border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="fit-analysis-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Analysing fit…</div>}
          {!analysisLoading && result && needsRefinement(result) && <RefinementPanel result={result} onSubmit={submitRefinement} loading={analysisLoading} />}
          {!analysisLoading && result && !needsRefinement(result) && verdictFor(result) && <FinalResult result={result} />}
          {!analysisLoading && result && !needsRefinement(result) && !verdictFor(result) && <NoVerdict onRetry={startAnalysis} />}
        </div>
      </div>
    </main>
  );
}
